/**
 * data/instrumentCatalog/bonds.ts
 * Bond instrument reference catalog
 * 
 * Contains core bond market instruments via liquid, retail-accessible ETFs
 * spanning government, corporate, municipal, international, and aggregate bonds.
 * 
 * Data source: Official fund pages and fact sheets from BlackRock, Vanguard, SPDR
 * Research file: docs/reference/InstrumentCatalogResearch/Bonds_DataPopulationResearch.md
 */

import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

/**
 * Bond instrument catalog
 * 
 * Contains 15 core bond market instruments via highly liquid ETFs:
 * - Government bonds (Treasury duration spectrum + TIPS)
 * - Corporate bonds (Investment Grade + High Yield)
 * - Municipal bonds (National AMT-free)
 * - Securitized bonds (Agency MBS)
 * - Aggregate bonds (Broad market building blocks)
 * - International bonds (Ex-US developed + EM sovereign)
 * 
 * All instruments are USD-denominated ETFs with standardized pricing.
 */
export const BONDS_INSTRUMENTS: InstrumentCatalogEntry[] = [
  
  // ===== US TREASURY BONDS =====
  
  {
    id: 'TLT_BONDS',
    symbol: 'TLT',
    name: '20+ Year Treasury Bond ETF',
    shortName: 'iShares 20+ Yr Treasury',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'government',
      subAssetClass: 'treasury'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.05,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Long-term Treasury exposure'
  },
  
  {
    id: 'IEF_BONDS',
    symbol: 'IEF',
    name: '7-10 Year Treasury Bond ETF',
    shortName: 'iShares 7-10 Yr Treasury',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'government',
      subAssetClass: 'treasury'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Intermediate Treasury exposure'
  },

  {
    id: 'SHY_BONDS',
    symbol: 'SHY',
    name: '1-3 Year Treasury Bond ETF',
    shortName: 'iShares 1-3 Yr Treasury',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'government',
      subAssetClass: 'treasury'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Short-term Treasury exposure'
  },

  {
    id: 'TIPS_BONDS',
    symbol: 'TIPS',
    name: 'Treasury Inflation Protected Securities ETF',
    shortName: 'iShares TIPS Bond',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'government',
      subAssetClass: 'tips'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Inflation-protected Treasuries'
  },

  // ===== CORPORATE BONDS =====

  {
    id: 'LQD_BONDS',
    symbol: 'LQD',
    name: 'Investment Grade Corporate Bond ETF',
    shortName: 'iShares IG Corporate',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'corporate',
      subAssetClass: 'investment-grade'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Investment grade corporate bonds'
  },

  {
    id: 'HYG_BONDS',
    symbol: 'HYG',
    name: 'High Yield Corporate Bond ETF',
    shortName: 'iShares High Yield Corp',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'corporate',
      subAssetClass: 'high-yield'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'High yield corporate bonds'
  },

  {
    id: 'JNK_BONDS',
    symbol: 'JNK',
    name: 'High Yield Bond ETF',
    shortName: 'SPDR Bloomberg High Yield',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'corporate',
      subAssetClass: 'high-yield'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Alternative high yield exposure'
  },

  // ===== MUNICIPAL BONDS =====

  {
    id: 'MUB_BONDS',
    symbol: 'MUB',
    name: 'National Municipal Bond ETF',
    shortName: 'iShares National Muni',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'municipal',
      subAssetClass: 'general-obligation'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Tax-exempt municipal bonds'
  },

  // ===== MORTGAGE-BACKED SECURITIES =====

  {
    id: 'MBB_BONDS',
    symbol: 'MBB',
    name: 'Mortgage-Backed Securities ETF TEST',
    shortName: 'iShares MBS',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'securitized',
      subAssetClass: 'mortgage-backed'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'EUR',
    notes: 'Agency mortgage-backed securities'
  },

  // ===== AGGREGATE BOND MARKET =====

  {
    id: 'AGG_BONDS',
    symbol: 'AGG',
    name: 'Core U.S. Aggregate Bond ETF',
    shortName: 'iShares Core Aggregate',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'aggregate',
      subAssetClass: 'broad-market'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Broad US bond market exposure'
  },

  {
    id: 'BND_BONDS',
    symbol: 'BND',
    name: 'Total Bond Market ETF',
    shortName: 'Vanguard Total Bond',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'aggregate',
      subAssetClass: 'broad-market'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Total bond market exposure'
  },

  // ===== INTERNATIONAL BONDS =====

  {
    id: 'BNDX_BONDS',
    symbol: 'BNDX',
    name: 'Total International Bond ETF',
    shortName: 'Vanguard Total Intl Bond',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'international',
      subAssetClass: 'developed-markets'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Ex-US developed market bonds'
  },

  {
    id: 'EMB_BONDS',
    symbol: 'EMB',
    name: 'Emerging Markets USD Bond ETF',
    shortName: 'iShares JP Morgan EM USD',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'international',
      subAssetClass: 'emerging-markets'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'USD-denominated EM sovereign bonds'
  },

  // ===== FLOATING RATE BONDS =====

  {
    id: 'FLOT_BONDS',
    symbol: 'FLOT1',
    name: 'Floating Rate Bond ETF',
    shortName: 'iShares Floating Rate',
    classification: {
      assetClass: 'bonds',
      marketSegment: 'corporate',
      subAssetClass: 'floating-rate'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Floating rate notes'
  }
];